import React from "react";
import { BsFileText } from "react-icons/bs";
import { FormattedMessage } from "react-intl";

import { Attachment } from "lib/build/attachments";

import { AttachmentTable } from "src/common/components/AttachmentTable";
import {
	Card,
	CardContent,
	CardHeader,
	CardMessage,
	CardTitle
} from "src/common/components/Card";
import { DateTimeInput } from "src/common/components/DateTime";
import { Field } from "src/common/components/Field";
import { Fieldset, Form, FormBody } from "src/common/components/Form";
import { GridColumn, GridRow } from "src/common/components/Grid";
import {
	MultiSelect,
	MultiSelectOption
} from "src/common/components/MultiSelect";
import { Popover } from "src/common/components/Popover";
import {
	FullCardPreloader,
	FullPagePreloader
} from "src/common/components/Preloader";
import {
	Timeline,
	TimelineItem,
	TimelineItemValue
} from "src/common/components/Timeline";
import { useTimelineItemsByEvents } from "src/features/events";
import { usePatientIdParam } from "src/features/patient";
import { genericMessages } from "src/messages/generic";
import { messages } from "src/views/patient-timeline/messages";
import { useConceptOptions } from "src/views/patient-timeline/useConceptOptions";
import { useEvents } from "src/views/patient-timeline/useEvents";
import { useFindEventsByTags } from "src/views/patient-timeline/useFindEventsByTags";
import { useInterpretations } from "src/views/patient-timeline/useInterpretations";
import { useTagOptions } from "src/views/patient-timeline/useTagOptions";

export const PatientTimeline: React.FunctionComponent = () => {
	const patientId = usePatientIdParam();

	const [isLoadingInterpretations, interpretations, fetchInterpretations] =
		useInterpretations();
	const [isLoadingEvents, events, fetchEvents] = useEvents();

	const [timeFrom, setTimeFrom] = React.useState<string | undefined>();
	const [timeTo, setTimeTo] = React.useState<string | undefined>();

	const conceptOptions = useConceptOptions(interpretations);
	const [concepts, setConcepts] = React.useState<string[]>([]);

	const tagOptions = useTagOptions(events);
	const [tags, setTags] = React.useState<string[]>([]);

	const eventsWithTags = useFindEventsByTags(events, tags);
	const timelineItems = useTimelineItemsByEvents(eventsWithTags);

	React.useEffect(() => {
		fetchInterpretations(patientId);
	}, [fetchInterpretations, patientId]);

	const eventsTimeoutRef = React.useRef(0);
	React.useEffect(() => {
		window.clearTimeout(eventsTimeoutRef.current);
		eventsTimeoutRef.current = window.setTimeout(() => {
			fetchEvents({ id: patientId, concepts, timeFrom, timeTo });
		}, 1500);
	}, [fetchEvents, patientId, timeFrom, timeTo, concepts]);

	React.useEffect(() => {
		setConcepts(conceptOptions.map(({ value }) => value));
	}, [conceptOptions]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: reset tag selection when available options change
	React.useEffect(() => {
		setTags([]);
	}, [tagOptions]);

	if (isLoadingInterpretations) {
		return <FullPagePreloader />;
	}

	return (
		<>
			<EventForm
				timeFrom={timeFrom}
				setTimeFrom={setTimeFrom}
				timeTo={timeTo}
				setTimeTo={setTimeTo}
				conceptOptions={conceptOptions}
				concepts={concepts}
				setConcepts={setConcepts}
				tagOptions={tagOptions}
				tags={tags}
				setTags={setTags}
			/>
			{concepts.length !== 0 && (
				<EventTimeline
					isLoading={isLoadingEvents}
					items={timelineItems}
				/>
			)}
		</>
	);
};

interface EventFormProps {
	timeFrom: string | undefined;
	setTimeFrom(value: string | undefined): unknown;

	timeTo: string | undefined;
	setTimeTo(value: string | undefined): unknown;

	conceptOptions: MultiSelectOption[];
	concepts: string[];
	setConcepts(value: string[]): unknown;

	tagOptions: MultiSelectOption[];
	tags: string[];
	setTags(value: string[]): unknown;
}

const EventForm: React.FunctionComponent<EventFormProps> = ({
	timeFrom,
	setTimeFrom,
	timeTo,
	setTimeTo,
	conceptOptions,
	concepts,
	setConcepts,
	tagOptions,
	tags,
	setTags
}) => (
	<Card>
		<CardHeader>
			<CardTitle>
				<FormattedMessage {...messages.title} />
			</CardTitle>
		</CardHeader>
		<CardContent>
			<Form
				isSubmitted={false}
				onSubmit={event => {
					event.preventDefault();
				}}
			>
				<FormBody>
					<Fieldset>
						<GridRow>
							<GridColumn columnSize={6}>
								<TimeFrom
									value={timeFrom}
									onChangeValue={setTimeFrom}
								/>
							</GridColumn>
							<GridColumn columnSize={6}>
								<Concepts
									options={conceptOptions}
									value={concepts}
									onChangeValue={setConcepts}
								/>
							</GridColumn>
						</GridRow>
						<GridRow>
							<GridColumn columnSize={6}>
								<TimeTo
									value={timeTo}
									onChangeValue={setTimeTo}
								/>
							</GridColumn>
							<GridColumn columnSize={6}>
								<Tags
									options={tagOptions}
									value={tags}
									onChangeValue={setTags}
								/>
							</GridColumn>
						</GridRow>
					</Fieldset>
				</FormBody>
			</Form>
		</CardContent>
	</Card>
);

const TimeFrom: React.FunctionComponent<
	React.ComponentProps<typeof DateTimeInput>
> = props => (
	<Field label={<FormattedMessage {...genericMessages.timeFrom} />}>
		<DateTimeInput {...props} />
	</Field>
);

const TimeTo: React.FunctionComponent<
	React.ComponentProps<typeof DateTimeInput>
> = props => (
	<Field label={<FormattedMessage {...genericMessages.timeTo} />}>
		<DateTimeInput {...props} />
	</Field>
);

const Concepts: React.FunctionComponent<
	React.ComponentProps<typeof MultiSelect>
> = props => (
	<Field label={<FormattedMessage {...messages.concepts} />}>
		<MultiSelect {...props} />
	</Field>
);

const Tags: React.FunctionComponent<
	React.ComponentProps<typeof MultiSelect>
> = props => (
	<Field label={<FormattedMessage {...messages.tags} />}>
		<MultiSelect {...props} />
	</Field>
);

interface EventTimelineProps {
	isLoading: boolean;
	items: TimelineItem<unknown>[];
}

const EventTimeline: React.FunctionComponent<EventTimelineProps> = ({
	isLoading,
	items
}) => (
	<Card>
		{isLoading ? (
			<FullCardPreloader />
		) : items.length === 0 ? (
			<CardMessage>
				<FormattedMessage {...messages.noEvents} />
			</CardMessage>
		) : (
			<CardContent>
				<Timeline
					items={items}
					renderActions={renderActions}
					renderContent={renderContent}
				/>
			</CardContent>
		)}
	</Card>
);

function renderActions({
	attachments
}: {
	attachments: Attachment[];
}): React.ReactNode {
	if (attachments.length === 0) {
		return null;
	}
	return (
		<Popover
			target={<BsFileText />}
			content={<AttachmentTable attachments={attachments} />}
		/>
	);
}

function renderContent({ data }: { data: unknown }): React.ReactNode {
	let content;
	if (typeof data === "object") {
		content = (
			<pre>
				{JSON.stringify(data, null, 2)
					.split("\n")
					.map((line, i) => (
						<div key={i}>{line}</div>
					))}
			</pre>
		);
	} else {
		content = `${data}`;
	}
	return <TimelineItemValue value={content} />;
}
